import React, { useState, useEffect, useRef } from "react";
import { Xmark, PencilToLine } from "@gravity-ui/icons";
import Version from "./Version";
import EditDetailsPanel from "./EditDetailsPanel";
import { useLocalState } from "../../contexts/LocalStateProvider";
import { usePostHog } from "posthog-js/react";
import ProgressInfoToast from "./ProgressInfoToast";
import Toast from "../generic_components/Toast"; // Add the Toast import

const DetailsPanel = ({
  className,
  onClose,
  projectDetails,
  onUpdateDetails,
}) => {
  const { localState, updateLocalState } = useLocalState();
  const posthog = usePostHog();

  const [isEditing, setIsEditing] = useState(false);
  const [details, setDetails] = useState(
    localState.currentSongDetails || projectDetails
  );
  const [versionHistory, setVersionHistory] = useState([]);
  const [stoppedVersionIds, setStoppedVersionIds] = useState(new Set());
  const versionHistoryRef = useRef(null);

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isGeneratingStems, setIsGeneratingStems] = useState(false);
  const [stemProgress, setStemProgress] = useState(0);
  const [stemTrackName, setStemTrackName] = useState('');
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");

  // Get the selection details object from localState
  const selectedTrackDetails = localState.selectedTrackDetails;
  const currentSongDetails = localState.currentSongDetails; 

  useEffect(() => {
    localState.isAudioPlaying
      ? setIsAudioPlaying(true)
      : setIsAudioPlaying(false);
  }, [localState.isAudioPlaying]);

  // Handle updates to details when state changes
  useEffect(() => {
    if (localState.versionHistory) {
      // Create a deep copy and filter out stopped versions immediately
      const filteredHistory = localState.versionHistory.map((group) => ({
        ...group,
        versions: group.versions.filter((version) => !version.isStopped && !stoppedVersionIds.has(version.ai_response_id)),
      }))
      // Only keep groups that still have versions
      .filter((group) => group.versions.length > 0);
    
      // Update local state with filtered history
      setVersionHistory(filteredHistory);
    }
  }, [localState.versionHistory, stoppedVersionIds]);

  // Implement the empty useEffect to track events being stopped
  useEffect(() => {
    if (localState.stoppedGenerationId) {
      // Add the stopped ID to our Set of stopped versions
      setStoppedVersionIds(prevIds => new Set([...prevIds, localState.stoppedGenerationId]));
    }
  }, [localState.stoppedGenerationId])

  // Scroll to the bottom when version history is updated
  useEffect(() => {
    if (versionHistoryRef.current) {
      versionHistoryRef.current.scrollTop =
        versionHistoryRef.current.scrollHeight;
    }
  }, [versionHistory]);

  // Handle updates to details when state changes
  useEffect(() => {
    if (localState.currentSongDetails) {
      setDetails(localState.currentSongDetails);
    } else {
      setDetails(projectDetails);
    }
  }, [localState.currentSongDetails]);

  const handleEditClick = () => {
    posthog.capture("Click on Edit Details", {
      is_user_logged_in: true
    });
    setIsEditing(true);
  };

  const handleSave = (updatedDetails) => {
    setDetails(updatedDetails);
    setIsEditing(false);
    if (onUpdateDetails) {
      onUpdateDetails(updatedDetails);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  useEffect(() => {
    if (localState.currentPlayingResponseId) {
      const versionElement = document.getElementById(
        `version-${localState.currentPlayingResponseId}`
      );
      if (versionElement && versionHistoryRef.current) {
        versionHistoryRef.current.scrollTo({
          top: versionElement.offsetTop - versionHistoryRef.current.offsetTop,
          behavior: "smooth",
        });
      }
    }
  }, [localState.currentPlayingResponseId]);

  // Add a function to handle closing the panel
  const handleClose = () => {
    // Close this panel
    updateLocalState("isDetailsPanelOpen", false);

    // Call the original onClose if provided
    if (onClose) {
      onClose();
    }
  };

  const handleStemGeneration = (trackName) => {
    setStemTrackName(trackName);
    setIsGeneratingStems(true);
    setStemProgress(0);

    // Simulate progress over 4 seconds
    const interval = setInterval(() => {
      setStemProgress(prev => {
        if (prev >= 4) {
          clearInterval(interval);
          setIsGeneratingStems(false);
          setToastMessage("Your stems have been downloaded successfully!");
          setToastType("success");
          setTimeout(() => setToastMessage(null), 3000);
          return 4;
        }
        return prev + 1;
      });
    }, 1000);
  };

  // Example usage in a stop generation handler
  const handleStopGeneration = (responseId) => {
    // Other stop logic here...
    
    // Update local state to trigger the removal from UI
    updateLocalState("stoppedGenerationId", responseId);
  };

  return isEditing ? (
    <EditDetailsPanel
      className={className}
      projectDetails={details}
      onSave={handleSave}
      onCancel={handleCancel}
      onClose={handleClose}
    />
  ) : (
    <div className={className}>
      <div className="flex items-center justify-between px-4 py-3">
        <h2 className="text-lg font-normal">Details</h2>
        <button
          className="text-[#878188] hover:text-white p-2 rounded-full hover:[background:var(--Color-Opacity-White-5,rgba(255,255,255,0.05))]"
          onClick={handleClose}
        >
          <Xmark />
        </button>
      </div>
      <div className="w-full border-t border-t-[#1d1b1d]"></div>

      <div className="flex flex-col items-center p-5">
        <div className="relative">
          <img
            src={details.coverImage}
            alt="Cover"
            className="w-full max-w-24 sm:max-w-36 md:max-w-40 lg:max-w-48 xl:max-w-52 2xl:max-w-64 3xl:max-w-80 aspect-square object-cover rounded-md mb-3"
          />
        </div>

        {!details?.state && (
          <div className="text-center w-full align-center justify-center items-center">
            <div className="flex justify-center items-center gap-1 mb-0.5 pl-8">
              <h3 className="font-extralight text-xl w-fit line-clamp-1">
                {details.name}
              </h3>

              <button
                className="text-[#878188] hover:text-white p-2 rounded-full hover:[background:var(--Color-Opacity-White-5,rgba(255,255,255,0.05))]"
                onClick={handleEditClick}
              >
                <PencilToLine />
              </button>
            </div>
            <p className="font-extralight self-center text-sm text-[#a5a0a6] w-full line-clamp-1">
              {details.description}
            </p>
          </div>
        )}
      </div>

      {isGeneratingStems ? (
        <ProgressInfoToast
          type="info"
          message={`${stemTrackName || 'Track'} - Stems are being generated...\nDrum stem in progress...`}
          loading={true}
          excludeCloseButton={false}
          className="-translate-x-1/2 w-[90%] md:w-fit md:min-w-[360px]"
          counter={`${stemProgress}/4`}
          onClose={() => setIsGeneratingStems(false)}
        />
      ) : toastMessage && (
        <Toast
          type={toastType}
          message={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}

      {!isEditing && (
        <div className="">
          <h4 className="font-normal text-base px-2.5 border-b border-b-[#2C2A2C] pb-2.5">
            History
          </h4>
          <div
            className={`p-2 flex-col ${isAudioPlaying &&
              (versionHistory?.some((group) => group.versions?.length >= 2) ||
                versionHistory?.length > 4)
              ? `max-h-[calc(100vh-300px)] sm:max-h-[calc(100vh-344px)] md:max-h-[calc(100vh-450px)] lg:max-h-[calc(100vh-480px)] xl:max-h-[calc(100vh-500px)] 2xl:max-h-[calc(100vh-548px)] scrollable-version overflow-y-auto`
              : (!isAudioPlaying &&
                versionHistory?.length > 1 &&
                versionHistory?.some(
                  (group) => group.versions?.length > 2
                )) ||
                (!isAudioPlaying &&
                  versionHistory?.length === 1 &&
                  versionHistory?.some(
                    (group) => group.versions?.length > 3
                  )) ||
                versionHistory?.length > 4
                ? `md:max-h-[calc(100vh-400px)] lg:max-h-[calc(100vh-416px)] xl:max-h-[calc(100vh-428px)] 2xl:max-h-[calc(100vh-472px)] scrollable-version overflow-y-auto`
                : "overflow-visible"
              }`}
            ref={versionHistoryRef}
          >
            {versionHistory
              // For each group, remove stopped versions from the versions array.
              .map((group) => ({
                ...group,
                versions: group.versions.filter(
                  (version) => !version.isStopped && !stoppedVersionIds.has(version.ai_response_id)
                ),
              }))
              // Only keep groups that still have at least one version.
              .filter((group) => group.versions.length > 0)
              .map((versionGroup, index) => {
                // Determine the track index to show for this specific version group.
                let trackIndexForThisGroup = null;


                // --- EDIT 1: Refine logic to include playingAIChatBoxTrackIndex ---
                // 1. Use selected_track_id from the latest version if available
                if (
                  versionGroup.latest &&
                  typeof versionGroup.latest.selected_track_id === "number"
                ) {
                  trackIndexForThisGroup = versionGroup.latest.selected_track_id;
                } else {
                  // 2. Fallback to previous logic (checkbox selection, preview, etc.)
                  const groupMatchesSelectedTrack = selectedTrackDetails &&
                    versionGroup.versions?.some(
                      (version) => String(version.ai_response_id) === String(selectedTrackDetails.responseId)
                    );

                  const groupMatchesCurrentSong = currentSongDetails &&
                    versionGroup.versions?.some(
                      (version) => String(version.ai_response_id) === String(currentSongDetails.ai_response_id)
                    );

                  if (groupMatchesSelectedTrack) {
                    trackIndexForThisGroup = selectedTrackDetails.trackIndex;
                  } else if (groupMatchesCurrentSong && currentSongDetails.playingAIChatBoxTrackIndex !== undefined) {
                    trackIndexForThisGroup = currentSongDetails.playingAIChatBoxTrackIndex;
                  } else if (groupMatchesCurrentSong && currentSongDetails.selectedTrackIndexForPreview !== undefined) {
                    trackIndexForThisGroup = currentSongDetails.selectedTrackIndexForPreview;
                  }
                }

                // Always render the Version component
                return (
                  <Version
                    key={versionGroup.ai_response_id || index}
                    version={versionGroup}
                    versionCount={versionHistory?.length - 1}
                    index={index}
                    onStemGeneration={handleStemGeneration}
                    trackIndexToShow={typeof trackIndexForThisGroup === "number" ? trackIndexForThisGroup : 0}
                  />
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailsPanel;
